import _ from 'underscore';
import Promise from 'bluebird';
import { CountryUtils, Constants } from '@bingads-webui-clientcenter/entity-utils';
import type { Country, DomainDataRecord, FindAllRepository } from './types';

let sortedCountries: Country[] | undefined;

/**
 * @type {country}
 * @param {string} country.Name Country localized name
 * @param {string} country.Code Country code
 * @param {string} country.CountryCallingCode Country calling code
 * @param {string} country.Currency Country currency
 * @param {number} country.Id Country id
 * @param {boolean} country.IsAddressRequired It indicates if country's address required
 * @param {boolean} country.IsVatApplicable It indicates if country's VAT applicable
 * @param {boolean} country.IsPaperlessBillingAllowed It indicates if country allow paperless billing
 */

/**
 * Get countries from MT domainData by calling controller: API/DomainData/CountryMapping
 * And sort country list by localiazed country name
 * @param {object} jsDataCcRest jsData object
 * @returns {promise<country[]>} The formatted countries
 */
export const findCountries = (jsDataCcRest: { DomainDataCountry: FindAllRepository<DomainDataRecord<Country>> }) => {
  if (_.isUndefined(sortedCountries)) {
    return jsDataCcRest.DomainDataCountry.findAll().then((countries) => {
      sortedCountries = CountryUtils.sortCountries(_.pluck(countries, 'value'));
      return sortedCountries;
    });
  }
  return Promise.resolve(sortedCountries);
};

/**
 * Get countries from MT domainData by calling controller: API/DomainData/CountryMapping
 * Group country list by calling code and sort by localiazed country name
 * @param {object} jsDataCcRest jsData object
 * @returns {promise<country[]>} The formatted countries
 */
export const findCountriesGroupByCallingCode = (jsDataCcRest: {
  DomainDataCountry: FindAllRepository<DomainDataRecord<Country>>;
}) => findCountries(jsDataCcRest).then((countries) => {
  const groupedCountries = CountryUtils.groupCountriesByCallingCode(countries);
  return CountryUtils.sortCountries(groupedCountries);
});

/**
 * Get allowedCountries mapping by accountMode
 * @param {object} jsDataCcRest jsData object
 * @returns {promise<object>} The accountMode allowedCountries mapping
 */
export const getAllowedCountriesByAccountModeMapping = (jsDataCcRest: {
  CountryMapping: FindAllRepository<DomainDataRecord<Country>>;
}) => jsDataCcRest.CountryMapping.findAll().then((countries) => {
  const escCountries = _.chain(countries)
    .pluck('value')
    .filter((c: Country) => c.HasESCSupport)
    .pluck('Code')
    .value();

  return _.any(escCountries)
    ? { [Constants.AccountMode.UnifiedSmart]: escCountries }
    : {};
});
